import React, { PropsWithChildren, useState } from 'react';
import { useQuery } from 'react-query';
import {
  checkAuth,
  getMappedSellers,
  CustomerDataType,
} from '../../services/auth';
import { Loader } from '../';
import { RegisterToSell } from '../../pages';
import TagManager from 'react-gtm-module';
import { QUERY_KEYS } from 'utils/constants';
export type VendorType = {
  sellerId: string;
  name: string;
};

type VendorDataType = CustomerDataType & {
  vendors: VendorType[];
  setCurrentVendor: (vendor: VendorType) => void;
};

// Auth token, Customer details, and function to setCurrentVendor provided globally
const AuthDataContext = React.createContext<VendorDataType>(undefined!);

export const Auth: React.FC<PropsWithChildren> = ({ children }) => {
  // Currently selected vendor from dropdown
  const [currentVendor, setCurrentVendor] = useState<VendorType | undefined>(
    undefined
  );

  // Initial Auth
  const authQuery = useQuery(QUERY_KEYS.AUTH, () => checkAuth(), {
    refetchOnWindowFocus: false,
  });

  // Get all vendors/sellers for user (from init auth)
  // Set the selected vendor on success
  const mappedSellersQuery = useQuery(
    [QUERY_KEYS.MAPPED_SELLERS_DATA, authQuery.data?.token],
    () => authQuery.data && getMappedSellers(authQuery.data.token),
    {
      enabled: !!authQuery.data?.token,
      onSuccess: (data) => {
        data && setCurrentVendor(data[0]);
        if (currentVendor !== undefined) {
          TagManager.dataLayer({
            dataLayer: {
              event: 'sellerIdTracking', // event name declared during initialization
              SellerId: currentVendor?.sellerId,
            },
          });
        }
        TagManager.dataLayer({
          dataLayer: {
            event: 'userIdTracking',
            UserId: authQuery.data?.userId,
          },
        });
      },
      refetchOnWindowFocus: false,
    }
  );

  // Get auth account details for selected vendor
  const sellersDataQuery = useQuery(
    [QUERY_KEYS.SELL_AUTH, currentVendor],
    () =>
      currentVendor &&
      checkAuth({
        data: [{ type: 'SellerId', value: currentVendor.sellerId }],
      }),
    {
      enabled: !!currentVendor,
      keepPreviousData: true,
      staleTime: 3_600_000, // 3600s, 1hr
      onSuccess: () => {
        TagManager.dataLayer({
          dataLayer: {
            event: 'sellerIdTracking',
            SellerId: currentVendor?.sellerId,
          },
        });
      },
    }
  );

  return (
    <Loader query={[authQuery, mappedSellersQuery, sellersDataQuery]}>
      {!!sellersDataQuery.data ? (
        <AuthDataContext.Provider
          value={{
            ...sellersDataQuery.data,
            vendors: mappedSellersQuery.data ?? [],
            setCurrentVendor,
          }}
        >
          {children}
        </AuthDataContext.Provider>
      ) : (
        !mappedSellersQuery.isSuccess ||
        (!!mappedSellersQuery.data && (
          <RegisterToSell title="Register to Start Selling with Grays" />
        ))
      )}
    </Loader>
  );
};

export const useAuthData = () => React.useContext(AuthDataContext);
